'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { certificatesDb, parseHistoryDb, seafarerProfileDb, userPrefsDb, type Certificate } from '@/lib/db';
import { mockCertificates } from '@/data/certificates';
import { buildCertificatesFromSeafarerData } from '@/lib/certificate-builder';
import { mergeCertificates } from '@/lib/profile-merge';
import { useAuth } from './AuthContext';
import { safeStorage } from '@/lib/safe-storage';

// Fire-and-forget: reschedule native local notifications after certs change.
// Only runs inside the Capacitor native app — no-op in browser.
async function autoScheduleNativeReminders(certs: Certificate[]) {
  try {
    const { scheduleCertificateLocalReminders } = await import('@/lib/native/localNotifications');
    const prefs = await userPrefsDb.get<{ alert90?: boolean; alert60?: boolean; alert30?: boolean; alert7?: boolean }>('notification_settings');
    const thresholds = [
      (prefs?.alert90 ?? true) && 90,
      (prefs?.alert60 ?? true) && 60,
      (prefs?.alert30 ?? true) && 30,
      (prefs?.alert7  ?? true) && 7,
    ].filter(Boolean) as number[];
    await scheduleCertificateLocalReminders(certs, thresholds);
  } catch {
    // Silently ignore — native module unavailable in browser
  }
}

// Bump this whenever the certificate-builder logic changes.
// CertificatesContext will force-rebuild from profile data when the stored version differs.
const CERT_VERSION = '17';

type CertificatesContextType = {
  certificates: Certificate[];
  isLoading: boolean;
  lastSyncedAt: string | null;
  /** DG import / rebuild path — merges incoming certs with existing manual/corrected records. */
  replaceCerts: (certs: Certificate[], fileName?: string) => Promise<void>;
  /** Direct user-mutation path — stores certs exactly as supplied, preserving caller's source. */
  saveCerts: (certs: Certificate[]) => Promise<void>;
  deleteCert: (id: string) => Promise<void>;
  refreshCerts: () => Promise<void>;
  syncFromProfile: () => Promise<{ count: number } | null>;
};

const CertificatesContext = createContext<CertificatesContextType>({
  certificates: [],
  isLoading: true,
  lastSyncedAt: null,
  replaceCerts: async () => {},
  saveCerts: async () => {},
  deleteCert: async () => {},
  refreshCerts: async () => {},
  syncFromProfile: async () => null,
});

export const useCertificates = () => useContext(CertificatesContext);

export function CertificatesProvider({ children }: { children: React.ReactNode }) {
  const { loading: authLoading } = useAuth();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  const loadFromDb = useCallback(async () => {
    try {
      const storedVersion = safeStorage.get('crewnest_cert_version');
      const stored = await certificatesDb.getAll();
      const versionMismatch = storedVersion !== CERT_VERSION;

      // If version is current and we have stored certs, use them as-is
      if (!versionMismatch && stored.length > 0) {
        setCertificates(stored);
        setLastSyncedAt(safeStorage.get('crewnest_last_sync'));
        return;
      }

      // Version bump OR no stored certs — rebuild from profile data, preserving manual certs
      const profileData = await seafarerProfileDb.get();
      if (profileData) {
        const built = buildCertificatesFromSeafarerData(profileData);
        if (built.length > 0) {
          const merged = mergeCertificates(stored, built);
          await certificatesDb.replaceAll(merged);
          const now = new Date().toISOString();
          safeStorage.set('crewnest_last_sync', now);
          safeStorage.set('crewnest_cert_version', CERT_VERSION);
          setCertificates(merged);
          setLastSyncedAt(now);
          return;
        }
      }

      // No profile data — if we had old certs use them, otherwise show mock data only for demo user
      const isDemo = !!safeStorage.get('crewnest_demo_user');
      if (stored.length > 0) {
        safeStorage.set('crewnest_cert_version', CERT_VERSION);
        setCertificates(stored);
        setLastSyncedAt(safeStorage.get('crewnest_last_sync'));
      } else {
        setCertificates(isDemo ? (mockCertificates as Certificate[]) : []);
      }
    } catch {
      const isDemo = !!safeStorage.get('crewnest_demo_user');
      setCertificates(isDemo ? (mockCertificates as Certificate[]) : []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Wait for auth to resolve so setActiveUserId() has been called before we open IndexedDB
  useEffect(() => {
    if (!authLoading) {
      loadFromDb();
    }
  }, [authLoading, loadFromDb]);

  const replaceCerts = useCallback(async (certs: Certificate[], fileName?: string) => {
    const existing = await certificatesDb.getAll();
    const merged = mergeCertificates(existing, certs);
    await certificatesDb.replaceAll(merged);
    const now = new Date().toISOString();
    safeStorage.set('crewnest_last_sync', now);
    safeStorage.set('crewnest_cert_version', CERT_VERSION);
    if (fileName) {
      await parseHistoryDb.add({ fileName, parsedAt: now, certCount: certs.length });
    }
    setCertificates(merged);
    setLastSyncedAt(now);
    autoScheduleNativeReminders(merged);
  }, []);

  const saveCerts = useCallback(async (certs: Certificate[]) => {
    await certificatesDb.replaceAll(certs);
    const now = new Date().toISOString();
    safeStorage.set('crewnest_last_sync', now);
    safeStorage.set('crewnest_cert_version', CERT_VERSION);
    setCertificates(certs);
    setLastSyncedAt(now);
    autoScheduleNativeReminders(certs);
  }, []);

  const deleteCert = useCallback(async (id: string) => {
    await certificatesDb.delete(id);
    setCertificates((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const refreshCerts = useCallback(async () => {
    setIsLoading(true);
    await loadFromDb();
  }, [loadFromDb]);

  // Manual sync: rebuild certificates from the stored profile data (without re-uploading PDF)
  const syncFromProfile = useCallback(async (): Promise<{ count: number } | null> => {
    const profileData = await seafarerProfileDb.get();
    if (!profileData) return null;
    const built = buildCertificatesFromSeafarerData(profileData);
    const existing = await certificatesDb.getAll();
    const merged = mergeCertificates(existing, built);
    await certificatesDb.replaceAll(merged);
    const now = new Date().toISOString();
    safeStorage.set('crewnest_last_sync', now);
    safeStorage.set('crewnest_cert_version', CERT_VERSION);
    setCertificates(merged);
    setLastSyncedAt(now);
    autoScheduleNativeReminders(merged);
    return { count: merged.length };
  }, []);

  const value = useMemo(
    () => ({ certificates, isLoading, lastSyncedAt, replaceCerts, saveCerts, deleteCert, refreshCerts, syncFromProfile }),
    [certificates, isLoading, lastSyncedAt, replaceCerts, saveCerts, deleteCert, refreshCerts, syncFromProfile]
  );

  return (
    <CertificatesContext.Provider value={value}>
      {children}
    </CertificatesContext.Provider>
  );
}
